import { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDto } from 'api/Clients/Types/Client'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { getClientFilterFields } from 'components/Filter/ClientFields'
import { SelectIntroducingBrokerModalOpenEvent } from 'components/Filter/IntroducingBrokersFields/SelectIntroducingBrokersFilter'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchAMLCategoriesTypes,
  fetchClientStatusAction,
  fetchClientTypesAction,
  fetchComplianceVulnerabilityGroupsAction,
  fetchTickmillProductsAction,
} from 'store/clients/clients.actions'
import {
  fetchAdministrationUsersAction,
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { IntroducingBrokersModal } from '../../IB/IntroducingBrokersModal'
import { SelectIntroducingBrokerEvent } from '../../IB/IntroducingBrokersTable'
import { clientsColumns } from './columns'
import { normalizeSortQuery } from './normalizer'

import '../RecordsClients.scss'

import { useNavigate } from 'react-router-dom'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'

const useMapStateToProps = () => ({
  clientTypes: useAppSelector((state) => state.clients.types),
  clientStatus: useAppSelector((state) => state.clients.status),
  complianceVulnerabilityGroups: useAppSelector(
    (state) => state.clients.complianceVulnerabilityGroups
  ),
  users: useAppSelector((state) => state.identity.administrationUsers),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  types: useAppSelector((state) => state.clients.types),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
  hasFilters: useAppSelector(selectGridFilters).length > 0,
  amlCategories: useAppSelector((state) => state.clients.amlCategories),
  countries: useAppSelector((state) => state.identity.countries),
  tickmillProducts: useAppSelector((state) => state.clients.tickmillProducts),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    fetchClientTypes: async () => dispatch(fetchClientTypesAction(clientsApiClient)),
    fetchClientStatus: async () => dispatch(fetchClientStatusAction(clientsApiClient)),
    fetchComplianceVulnerabilityGroups: async () =>
      dispatch(fetchComplianceVulnerabilityGroupsAction(clientsApiClient)),
    fetchUsers: async () => dispatch(fetchAdministrationUsersAction(identityApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    removeFilter: async (filterId: string) =>
      dispatch(removeFilterAction(filterId, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchAMLCategories: async () => dispatch(fetchAMLCategoriesTypes(clientsApiClient)),
    fetchTickmillProducts: async () => dispatch(fetchTickmillProductsAction(clientsApiClient)),
  }
}

export const RecordsClientsPage = (): JSX.Element => {
  const [clients, setClients] = useState<ClientDto[]>([])
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [auth] = useContext(AuthSessionContext)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const permissions = auth?.permissions || []
  const crumbs = [{ title: 'Clients', link: links.recordsClients.url }]
  const navigate = useNavigate()

  const [isIntroducingBrokersModalOpen, setIntroducingBrokersModalOpen] = useState<boolean>(false)
  const [introducingBrokerFilterId, setIntroducingBrokerFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    filters,
    filterTypes,
    users,
    clientStatus,
    clientTypes,
    tickmillCompanies,
    gridFilterByFilterIdSelector,
    complianceVulnerabilityGroups,
    amlCategories,
    countries,
    tickmillProducts,
  } = useMapStateToProps()
  const {
    fetchClientTypes,
    fetchClientStatus,
    fetchUsers,
    fetchFilters,
    fetchCountries,
    fetchFilterTypes,
    fetchTickmillCompanies,
    fetchComplianceVulnerabilityGroups,
    fetchAMLCategories,
    fetchTickmillProducts,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<ClientDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await clientsApiClient.getClients({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(clientTypes)) await fetchClientTypes()
          if (isEmpty(clientStatus)) await fetchClientStatus()
          if (isEmpty(users)) await fetchUsers()
          if (isEmpty(filterTypes)) await fetchFilterTypes()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()
          if (isEmpty(complianceVulnerabilityGroups)) await fetchComplianceVulnerabilityGroups()
          if (isEmpty(filters)) await fetchFilters()
          if (isEmpty(amlCategories)) await fetchAMLCategories()
          if (isEmpty(countries)) await fetchCountries()
          if (isEmpty(tickmillProducts)) fetchTickmillProducts()

          if (response?.items) {
            setClients(response?.items)
          }

          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<ClientDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        take: gridFilterState.take,
        skip: gridFilterState.skip,
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor): string | null => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientsApiClient.getClients({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setClients(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // TODO: udpate clientsAPIClient so that we can include it in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getClients({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setClients(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await clientsApiClient.getClients({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getClients({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientsApiClient.getClients({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setClients(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleIntroducingBrokersSelect = (event: SelectIntroducingBrokerEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleIntroducingBrokersModalOpen = (event: SelectIntroducingBrokerModalOpenEvent) => {
    setIntroducingBrokersModalOpen(true)
    setIntroducingBrokerFilterId(event.filterId)
  }

  const handleIntroducingBrokersModalClose = () => {
    setIntroducingBrokersModalOpen(false)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isIntroducingBrokersModalOpen && (
        <IntroducingBrokersModal
          filterId={introducingBrokerFilterId}
          onSelect={handleIntroducingBrokersSelect}
          onClose={handleIntroducingBrokersModalClose}
        />
      )}
      <Breadcrumbs crumbs={crumbs} />
      <div className='records-clients'>
        <SelectFiltersFacade
          setGridFilters={gridActions.setGridFilters}
          filterFetch={filterFetch}
          filterType={FilterType.CLIENT}
          gridState={gridState}
          filterFields={getClientFilterFields(handleIntroducingBrokersModalOpen)}
        />
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <TableWithExport
            data={clients}
            columns={clientsColumns}
            hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportClientsModule)}
          >
            <Grid
              {...gridState}
              resizable
              data={clients}
              sort={gridState.sort}
              total={gridState.total}
              take={gridState.take}
              skip={gridState.skip}
              onSortChange={handleSortChange}
              pageable={true}
              pager={() => (
                <Pager
                  onSkipCountChange={handleSkipCountChange}
                  onPageChange={handleGridPageChange}
                  onPageSizeChange={handlePageSizeChange}
                  page={gridState.pageIndex}
                  pageSize={gridState.pageSize}
                  take={gridState.take || 10}
                  skip={gridState.skip || 0}
                />
              )}
              style={{ zIndex: 0 }}
            >
              {clientsColumns
                .filter((c) => c.show)
                .map((column, idx) => (
                  <Column key={idx + 2} {...column} />
                ))}
              <Column
                width={50}
                title=''
                cell={(gridCellProps: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Create Transaction',
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanCreateTransactions
                          ),
                          onClick: () =>
                            navigate(links.createTransaction.url, {
                              state: {
                                client: gridCellProps.dataItem,
                              },
                            }),
                        },
                      ]}
                    />
                  </td>
                )}
              />
            </Grid>
          </TableWithExport>
        </TableStateContext.Provider>
      </div>
    </>
  )
}
